import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'

import {
  Slider, // year slider if needed in future
  Box,
  Container,
  Typography,
  Button,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Grid,
  useMediaQuery,
  useTheme,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  Divider,
  Chip,
  Stack,
  Breadcrumbs,
  Link,
  Fab,
  Zoom,
  OutlinedInput,
  Checkbox,
  ListItemText,
  InputAdornment,
  IconButton,
  Collapse,
  Tooltip,
} from '@mui/material'
import Pagination from '../Pagination'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import ClearIcon from '@mui/icons-material/Clear'
import CustomFormControl from '../CustomFormControl'
import CustomTextField from '../CustomTextField'
import ArticleCard from '../laws/ArticleCard'
import GuideCard from '../guides/GuideCard'
import DecisionCard from '../decisions/DecisionCard'
// import usePageTracking from '../../services/usePageTracking'
import apiService from '../../services/ApiService'
import { activityTracker } from '../../services/ActivityTracker'
import ItemsPerPageSelector from '../ItemsPerPageSelector'
import { useCountryLaw } from '../../context/CountryLawContext'
import { getDomainInfo, getCountryFromDomain } from '../../utils/domainUtils'
import { useAuth } from '../../context/AuthContext'
import useAutocompleteRequestKey from '../../hooks/useAutocompleteRequestKey'
import NoResultsModal from '../NoResultsModal'
/**
 * TabPanel component for showing content based on selected tab
 */
function TabPanel(props) {
  const { children, value, index, ...other } = props
  //   Add a state and useEffect to track if this tab has ever been selected - Lazy loading tab panels (loading all tab content simultaneously is inefficient when the user only sees one tab at a time)
  const [rendered, setRendered] = useState(value === index)
  // Update rendered state when tab becomes active
  useEffect(() => {
    if (value === index) {
      setRendered(true)
    }
  }, [value, index])
  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`search-tabpanel-${index}`}
      aria-labelledby={`search-tab-${index}`}
      {...other}
    >
      {/* {value === index && <Box sx={{ py: 3 }}>{children}</Box>} */}
      {rendered && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  )
}
const GCC_ORG_FLAG_URL = 'https://flagpedia.net/data/org/w20/gcc.webp'
const GCC_ORG_FLAG_URL_2X = 'https://flagpedia.net/data/org/w40/gcc.webp'
const GCC_FLAG_CODES = {
  bahrain: 'bh', ksa: 'sa', kuwait: 'kw', oman: 'om', qatar: 'qa', uae: 'ae',
}

// Define available years - this could be dynamic from an API
const AVAILABLE_YEARS = [
  '2018',
  '2019',
  '2020',
  '2021',
  '2022',
  '2023',
  '2024',
  '2025',
  '2026',
]
const decisionTypeColors = {
  // Primary Colors
  'CD - Cabinet Decision': {
    main: '#4e342e', // Brown
    light: '#efebe9',
  },
  'MD - Ministerial Decision': {
    main: '#1976d2', // Blue
    light: '#e3f2fd',
  },
  'FTA - Federal Tax Authority Decision': {
    main: '#2e7d32', // Green
    light: '#e8f5e9',
  },
  'AD - Administrative Decision': {
    main: '#4e342e', // Brown
    light: '#efebe9',
  },
  'ZD - ZATCA Decision': {
    main: '#d81b60', // Pink
    light: '#fce4ec',
  },
  'BL - Bylaws': {
    main: '#0097a7', // Teal
    light: '#e0f7fa',
  },
  'ER - Executive Rules and Instructions': {
    main: '#673ab7',
    light: '#ede7f6',
  },
  'ERS - Executive Regulations': {
    main: '#673ab7',
    light: '#ede7f6',
  },
  'IR - Implementing Regulations': {
    main: '#673ab7',
    light: '#ede7f6',
  },
  'EB - Executive Bylaws': {
    main: '#673ab7',
    light: '#ede7f6',
  },
  'JD - Judicial Decision': {
    main: '#673ab7',
    light: '#ede7f6',
  },
  default: {
    main: '#673ab7', // Default Purple
    light: '#ede7f6',
  },
}
const guidelineTypeColors = {
  'GUIDE - Federal Tax Authority Guide': {
    main: '#3f51b5', // Blue for FTA guides
    light: '#e8eaf6',
  },
  'GUIDE - Federal Tax Authority Service Guide': {
    main: '#d81b60',
    light: '#fce4ec',
  },
  'PC - Public Clarification': {
    main: '#f44336', // Red for Public Clarifications
    light: '#ffebee',
  },
  'GUIDE - Zakat, Tax and Customs Authority': {
    main: '#2e7d32', // Green for MoF guides
    light: '#e8f5e9',
  },
  'GUIDE - Foreign Account Tax Compliance Act Guide': {
    main: '#673ab7',
    light: '#ede7f6',
  },
  'MANUAL - User Manual': {
    main: '#ff9800', // Orange for Cabinet Decisions
    light: '#fff3e0',
  },
  'GUIDE - VAT Taxpayer Guide': {
    main: '#0097a7', // Teal
    light: '#e0f7fa',
  },
  'CIRCULAR - Circular': {
    main: '#1976d2', // Blue
    light: '#e3f2fd',
  },
  'TBC - Tax Bulletin and Circular': {
    main: '#d81b60', // Red for Tax Bulletin and Circular
    light: '#fce4ec',
  },
  'GUIDE - Oman Tax Authority': {
    main: '#37474f', // Blue Grey
    light: '#eceff1',
  },
  'GUIDE - Qatar Tax Authority': {
    main: '#37474f', // Blue Grey
    light: '#eceff1',
  },
  'GUIDE - National Bureau for Revenue': {
    main: '#37474f', // Blue Grey
    light: '#eceff1',
  },
  // Default color for any other types
  default: {
    main: '#9e9e9e', // Grey
    light: '#f5f5f5',
  },
}
// Menu props for the multi-select dropdowns
const ITEM_HEIGHT = 36 // 48
const ITEM_PADDING_TOP = 4 // 8

/**
 * Parse the leading numeric prefix of a string as a number. Used by the
 * sortedArticles / sortedDecisions useMemos so "100" sorts after "21"
 * instead of before (lexicographic vs numeric). Mirrors the backend's
 * parseLeadingNumeric in SearchV2Service. Examples:
 *   "21"        → 21
 *   "21.3"      → 21.3
 *   "47(1)"     → 47
 *   "47 of 22"  → 47
 *   null / ""   → 0
 */
const parseLeadingNumeric = (raw) => {
  if (raw === null || raw === undefined) return 0
  const trimmed = String(raw).trim()
  if (!trimmed) return 0
  const m = trimmed.match(/^(\d+(?:\.\d+)?)/)
  if (!m) return 0
  const value = Number(m[1])
  return Number.isFinite(value) ? value : 0
}

/**
 * SearchSideBar component for filtering search results
 */
const SearchSideBar = ({
  selectedCountry,
  setSelectedCountry,
  selectedLawShortName,
  setSelectedLawShortName,
  searchQuery,
  setSearchQuery,
  selectedYears,
  setSelectedYears,
  handleSearch,
  handleReset,
  loading,
  updateUrlParams,
  setCurrentArticlePage,
  setCurrentDecisionPage,
  setCurrentFtaGuidePage,
  setResults,
  searchRequestRef,
  setLoading,
  hasInitializedRef,
  isUrlTriggeredRef,
  isAuthenticated,
  autocompleteEnabled = true,
}) => {
  const theme = useTheme()
  const domainInfo = getDomainInfo()
  const showCountrySelector = domainInfo.shouldShowCountrySelector

  // Use the context to get country and law data
  const { countriesWithLaws, availableLaws, setAvailableLaws } = useCountryLaw()

  const [error, setError] = useState(null)

  const [tempSearchQuery, setTempSearchQuery] = useState(searchQuery || '')
  const [selectedAutocompleteSuggestion, setSelectedAutocompleteSuggestion] =
    useState('')
  const [autocompleteResults, setAutocompleteResults] = useState({})
  const [autocompleteLoading, setAutocompleteLoading] = useState(false)
  const [showAutocomplete, setShowAutocomplete] = useState(false)
  const autocompleteContext = useMemo(
    () => ({
      country: selectedCountry,
      lawShortName: selectedLawShortName,
    }),
    [selectedCountry, selectedLawShortName],
  )
  const {
    debouncedQuery: debouncedAutocompleteQuery,
    requestKey: autocompleteRequestKey,
  } = useAutocompleteRequestKey({
    query: tempSearchQuery,
    context: autocompleteContext,
    debounceMs: 300,
    minLength: 2,
  })

  // State for showing/hiding advanced filters
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)
  // const [yearRange, setYearRange] = useState([2018, 2025]) // year slider if needed in future
  // Theme colors for consistent styling
  const isSearchQueryDisabled =
    !isAuthenticated || loading || !selectedLawShortName
  const themeColors = {
    primary: '#232536',
    secondary: '#ffcf51',
    hover: '#3a3e5a',
    background: '#f8f8f8',
    border: '#e0e0e0',
  }

  // Update laws when a country changes - option available only for gcctaxlaws.com domain
  useEffect(() => {
    if (countriesWithLaws.length > 0 && selectedCountry) {
      const selectedCountryData = countriesWithLaws.find(
        (country) =>
          country.countryName.toLowerCase() === selectedCountry.toLowerCase(),
      )
      if (selectedCountryData && selectedCountryData.laws) {
        setAvailableLaws(selectedCountryData?.laws || [])

        // Don't set default law if selectedLawShortName is already set, This respects URL parameters
        // if (!selectedLawShortName) {
        //   // setSelectedLawShortName('') // Reset law selection when country changes
        //   const defaultLaw = selectedCountryData.laws[0]
        //   setSelectedLawShortName(defaultLaw?.lawShortName || '')
        // }
      } else {
        // consider we visit https://uaetaxlaws.com/search-across-law?country=GCC&law=common-vat-agreement, but uaetaxlaws.com will only get data for uae region from backend and on globalSearch page we dont have option to change country as well then how can user see any uae law data??
        // If no laws found, set empty array
        setAvailableLaws([])
        setSelectedLawShortName('')
      }
    } else {
      setAvailableLaws([])
    }
  }, [selectedCountry, countriesWithLaws, selectedLawShortName])

  // Update tempSearch query when parent search query changes
  useEffect(() => {
    setTempSearchQuery(searchQuery || '')
    setSelectedAutocompleteSuggestion('')
  }, [searchQuery])

  // Get the lawFullName for the selectedLawShortName
  const selectedLawFullName = useMemo(() => {
    return (
      availableLaws.find(
        (law) =>
          law.lawShortName.toLowerCase() === selectedLawShortName.toLowerCase(),
      )?.lawFullName || ''
    )
  }, [availableLaws, selectedLawShortName])

  // Handle search button click
  const handleSearchClick = () => {
    // Store query in a variable to ensure consistency
    const query = tempSearchQuery

    // Update parent state AND trigger search with the same query value
    setSearchQuery(query)

    // Call parent handler with the SAME query
    handleSearch(query, selectedAutocompleteSuggestion || null)
    setShowAutocomplete(false)
  }
  // Handle country selection change
  const handleCountryChange = (e) => {
    // Don't allow country changes on country-specific domains
    if (domainInfo.isCountrySpecific) {
      return
    }
    const newCountry = e.target.value
    setSelectedCountry(newCountry)

    if (newCountry !== '') {
      // Reset law when country changes
      setSelectedLawShortName('')

      // Clear search query
      setTempSearchQuery('')
      setSelectedAutocompleteSuggestion('')
      setAutocompleteResults({})
      setShowAutocomplete(false)
      setSearchQuery('')

      setSelectedYears([])

      // Update URL with new country but no law or query
      updateUrlParams(newCountry, '', '', [])

      // Reset pagination
      setCurrentArticlePage(1)
      setCurrentDecisionPage(1)
      setCurrentFtaGuidePage(1)

      setResults({
        articles: [],
        decisions: [],
        guidelines: [],
      })
    }
  }

  // Handle law selection change
  const handleLawChange = (e) => {
    const newLawShortName = e.target.value
    setSelectedLawShortName(newLawShortName)

    // If a valid law is selected, reset search state and trigger a search
    if (newLawShortName !== '') {
      // Clear search query when law changes
      setTempSearchQuery('')
      setSelectedAutocompleteSuggestion('')
      setAutocompleteResults({})
      setShowAutocomplete(false)
      setSearchQuery('')

      setSelectedYears([])

      // Update URL with new law but no query
      updateUrlParams(selectedCountry, newLawShortName, '', [])

      // Reset results pagination
      setCurrentArticlePage(1)
      setCurrentDecisionPage(1)
      setCurrentFtaGuidePage(1)

      setResults({
        articles: [],
        decisions: [],
        guidelines: [],
      })
      // Only trigger search if we've already initialized and this isn't a URL-triggered change
      if (hasInitializedRef.current && !isUrlTriggeredRef.current) {
        // Create search request WITHOUT timestamp (timestamp will be created in useEffect)
        const currentRequest = {
          country: selectedCountry,
          lawShortName: newLawShortName,
          query: '', // Empty query
          // timestamp: searchTimestamp, // timestamp will be created in useEffect
        }

        // Store request
        searchRequestRef.current = currentRequest

        // Trigger new search
        setLoading(true)
      }
    }
    // Reset URL trigger flag
    isUrlTriggeredRef.current = false
  }
  // Handle years selection change
  const handleYearsChange = (event) => {
    const { value } = event.target
    const newYears = typeof value === 'string' ? value.split(',') : value

    setSelectedYears(newYears)

    // Update URL with selected years
    updateUrlParams(
      selectedCountry,
      selectedLawShortName,
      searchQuery,
      newYears,
    )
    setCurrentArticlePage(1)
    setCurrentDecisionPage(1)
    setCurrentFtaGuidePage(1)
  }

  const universalSearchUrl = `/search-across-country?country=${selectedCountry}&query=${encodeURIComponent(
    tempSearchQuery || '',
  )}`

  useEffect(() => {
    let cancelled = false
    if (!isAuthenticated || !autocompleteEnabled) {
      setAutocompleteResults({})
      setShowAutocomplete(false)
      setAutocompleteLoading(false)
      return undefined
    }

    const normalizedQuery = tempSearchQuery?.trim() || ''
    if (!normalizedQuery) {
      setAutocompleteResults({})
      setShowAutocomplete(false)
      setAutocompleteLoading(false)
      return undefined
    }

    if (!selectedCountry || !selectedLawShortName) {
      setAutocompleteResults({})
      setShowAutocomplete(false)
      setAutocompleteLoading(false)
      return undefined
    }

    const timer = setTimeout(async () => {
      try {
        setAutocompleteLoading(true)
        const response = await apiService.globalSearchTaxLawsAutocomplete({
          q: normalizedQuery,
          law: selectedLawShortName,
          country: selectedCountry,
        })
        if (cancelled) return
        const safeResponse =
          response && typeof response === 'object' ? response : {}
        setAutocompleteResults(safeResponse)
        setShowAutocomplete(Object.keys(safeResponse).length > 0)
      } catch {
        if (cancelled) return
        setAutocompleteResults({})
        setShowAutocomplete(false)
      } finally {
        if (!cancelled) {
          setAutocompleteLoading(false)
        }
      }
    }, 300)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [tempSearchQuery, isAuthenticated, selectedCountry, selectedLawShortName, autocompleteEnabled])

  const autocompleteItems = useMemo(() => {
    const mappingPriority = {
      canonical: 4,
      explicit: 3,
      equivalent: 2,
      unknown: 1,
    }
    const dedupedByValue = new Map()
    const normalizedTypedQuery = (tempSearchQuery || '').trim().toLowerCase()

    Object.entries(autocompleteResults || {}).forEach(([sourceTerm, value]) => {
      if (!Array.isArray(value)) return

      value.forEach((item) => {
        const normalizedSource = String(sourceTerm || '').toLowerCase()
        const fallbackMappingType = normalizedSource.includes('canonical')
          ? 'canonical'
          : 'unknown'

        const mappingType =
          typeof item === 'string'
            ? fallbackMappingType
            : (typeof item?.mappingType === 'string'
                ? item.mappingType
                : fallbackMappingType
              ).toLowerCase()

        const candidates =
          typeof item === 'string'
            ? [item]
            : [
                ...(Array.isArray(item?.input) ? item.input : []),
                ...(Array.isArray(item?.synonyms) ? item.synonyms : []),
              ]

        candidates.forEach((candidate) => {
          if (typeof candidate !== 'string') return
          const trimmedCandidate = candidate.trim()
          if (!trimmedCandidate) return

          const dedupeKey = trimmedCandidate.toLowerCase()
          const existing = dedupedByValue.get(dedupeKey)

          if (
            !existing ||
            (mappingPriority[mappingType] || 0) >
              (mappingPriority[existing.mappingType] || 0)
          ) {
            dedupedByValue.set(dedupeKey, {
              value: trimmedCandidate,
              mappingType,
              sourceTerm,
            })
          }
        })
      })
    })

    const items = Array.from(dedupedByValue.values())
      .sort((a, b) => {
        const aExact = a.value.toLowerCase() === normalizedTypedQuery ? 0 : 1
        const bExact = b.value.toLowerCase() === normalizedTypedQuery ? 0 : 1
        if (aExact !== bExact) return aExact - bExact

        if (a.mappingType !== b.mappingType) {
          return (
            (mappingPriority[b.mappingType] || 0) -
            (mappingPriority[a.mappingType] || 0)
          )
        }
        return a.value.localeCompare(b.value, undefined, {
          sensitivity: 'base',
        })
      })
      .slice(0, 20)

    return items
  }, [autocompleteResults, tempSearchQuery])

  return (
    <Paper
      elevation={2}
      sx={{
        padding: '16px',
        backgroundColor: themeColors.background,
        borderRadius: '8px',
        height: 'fit-content',
        transition: 'box-shadow 0.3s ease',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        },
      }}
    >
      <Typography
        variant='h5'
        sx={{
          mb: 2,
          color: themeColors.primary,
          fontWeight: 500,
          letterSpacing: '0.02em',
        }}
      >
        Quick Filters
      </Typography>

      <Divider sx={{ mb: 2 }} />

      {error && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Conditionally render country selector */}
      {showCountrySelector && (
        <CustomFormControl size='small' fullWidth margin='normal'>
          <InputLabel shrink={Boolean(selectedCountry)}>
            Select Jurisdiction
          </InputLabel>
          <Select
            value={selectedCountry || ''}
            onChange={handleCountryChange}
            label='Select Jurisdiction'
            required
            renderValue={(value) => value}
            disabled={loading} // disabled={isLoadingCountries || loading} // disabled={true}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: themeColors.border,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: themeColors.primary,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: themeColors.primary,
              },
            }}
          >
            {/* {selectedCountry ? (
            <MenuItem value={selectedCountry}>{selectedCountry}</MenuItem>
          ) : (
            <MenuItem value=''>Select Jurisdiction</MenuItem>
          )} */}

            <MenuItem value=''>Select Jurisdiction</MenuItem>
            {countriesWithLaws.map((country) => {
              const nameLower = country.countryName.toLowerCase()
              const flagCode = GCC_FLAG_CODES[nameLower]
              const isGCC = nameLower === 'gcc'
              return (
                <MenuItem
                  key={country.countrySlug || country.id || country.countryName}
                  value={country.countryName}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  {(flagCode || isGCC) && (
                    <img
                      loading='lazy'
                      width='20'
                      src={isGCC ? GCC_ORG_FLAG_URL : `https://flagcdn.com/w20/${flagCode}.png`}
                      srcSet={isGCC ? `${GCC_ORG_FLAG_URL_2X} 2x` : `https://flagcdn.com/w40/${flagCode}.png 2x`}
                      alt=''
                      style={{ flexShrink: 0 }}
                    />
                  )}
                  {country.countryName}
                </MenuItem>
              )
            })}
          </Select>
        </CustomFormControl>
      )}

      <CustomFormControl size='small' fullWidth margin='normal'>
        <InputLabel shrink={Boolean(selectedLawShortName)}>
          Select Law (Required)
        </InputLabel>
        <Select
          value={selectedLawShortName || ''}
          onChange={handleLawChange}
          required
          label='Select Law (Required)'
          disabled={!selectedCountry || availableLaws.length === 0 || loading}
          renderValue={() => selectedLawFullName || selectedLawShortName}
          sx={{
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: themeColors.border,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: themeColors.primary,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: themeColors.primary,
            },
          }}
        >
          <MenuItem value=''>Select Law</MenuItem>
          {[...availableLaws]
            .sort((a, b) =>
              (a.lawFullName || '').localeCompare(
                b.lawFullName || '',
                undefined,
                { sensitivity: 'base' },
              ),
            )
            .map((law) => (
              <MenuItem
                key={law.slug || law.id || law.lawShortName}
                value={law.lawShortName}
              >
                {law.lawFullName}
              </MenuItem>
            ))}
        </Select>
      </CustomFormControl>

      <Tooltip
        title={!isAuthenticated ? 'Please login to use search query' : ''}
        arrow
        placement='top'
        disableHoverListener={isAuthenticated}
        disableFocusListener={isAuthenticated}
        disableTouchListener={isAuthenticated}
      >
        <Box
          component='span'
          sx={{
            display: 'block',
            width: '100%',
            cursor: !isAuthenticated ? 'not-allowed' : 'text',
          }}
        >
          <CustomTextField
            size='small'
            label='Enter Search Query'
            value={tempSearchQuery}
            onChange={(e) => {
              setTempSearchQuery(e.target.value)
              setSelectedAutocompleteSuggestion('')
              setShowAutocomplete(true)
            }}
            fullWidth
            margin='normal'
            onKeyDown={(e) => {
              if (
                e.key === 'Enter' &&
                !loading &&
                selectedCountry &&
                selectedLawShortName &&
                tempSearchQuery.trim() &&
                !isSearchQueryDisabled
              ) {
                handleSearchClick()
                setShowAutocomplete(false)
              }
            }}
            disabled={isSearchQueryDisabled}
            inputProps={{
              'data-autocomplete-key': autocompleteRequestKey,
              'data-debounced-query': debouncedAutocompleteQuery,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: themeColors.primary,
                },
                '&:hover fieldset': {
                  borderColor: themeColors.secondary,
                },
                '&.Mui-focused fieldset': {
                  borderColor: themeColors.primary,
                },
              },
              '& .MuiInputBase-root.Mui-disabled': {
                cursor: 'not-allowed',
              },
              '& .MuiInputBase-input.Mui-disabled': {
                cursor: 'not-allowed',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchRoundedIcon color='action' />
                </InputAdornment>
              ),
              endAdornment: tempSearchQuery ? (
                <InputAdornment position='end'>
                  {autocompleteLoading && (
                    <CircularProgress
                      size={16}
                      sx={{ color: themeColors.primary, mr: 1 }}
                    />
                  )}
                  <IconButton
                    // size='small'
                    aria-label='clear search'
                    onClick={() => {
                      setTempSearchQuery('')
                      setSelectedAutocompleteSuggestion('')
                      setAutocompleteResults({})
                      setShowAutocomplete(false)
                    }}
                    edge='end'
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>
      </Tooltip>
      {showAutocomplete && autocompleteItems.length > 0 && (
        <Paper
          elevation={3}
          sx={{
            mt: 0.5,
            maxHeight: 280,
            overflowY: 'auto',
            borderRadius: '8px',
            border: `1px solid ${themeColors.border}`,
          }}
        >
          {autocompleteItems.map((item) => (
            <Box
              key={`${item.mappingType}-${item.value}`}
              onClick={() => {
                setTempSearchQuery(item.value)
                setSelectedAutocompleteSuggestion(item.value)
                setShowAutocomplete(false)
              }}
              sx={{
                px: 1.5,
                py: 1.2,
                cursor: 'pointer',
                borderBottom: `1px solid ${themeColors.border}`,
                '&:last-of-type': {
                  borderBottom: 'none',
                },
                '&:hover': {
                  bgcolor: '#f6f6f6',
                },
              }}
            >
              <Typography variant='body2' sx={{ color: themeColors.primary }}>
                {item.value}
              </Typography>
            </Box>
          ))}
        </Paper>
      )}
      {!isAuthenticated && (
        <Typography
          variant='caption'
          sx={{
            display: 'block',
            mt: -1,
            mb: 1,
            color: themeColors.primary,
            fontWeight: 600,
          }}
        >
          Login to use search query.
        </Typography>
      )}

      <Box
        display='flex'
        justifyContent='space-between'
        mt={3}
        mb={1}
        sx={{ gap: 2 }}
      >
        <Button
          variant='contained'
          onClick={handleSearchClick}
          startIcon={
            loading ? (
              <CircularProgress size={20} color='inherit' />
            ) : (
              <SearchRoundedIcon />
            )
          }
          disabled={
            !selectedCountry ||
            !selectedLawShortName ||
            loading ||
            selectedLawShortName === 'coming-soon'
          }
          sx={{
            color: '#fff',
            bgcolor: themeColors.primary,
            fontWeight: 500,
            flexGrow: 1,
            ':hover': {
              color: themeColors.primary,
              bgcolor: themeColors.secondary,
            },
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            borderRadius: '4px',
          }}
        >
          {loading ? 'Searching...' : 'Search'}
        </Button>

        <Button
          variant='outlined'
          onClick={handleReset}
          startIcon={<RestartAltRoundedIcon />}
          disabled={loading}
          sx={{
            color: themeColors.primary,
            bgcolor: '#fff',
            borderColor: themeColors.primary,
            fontWeight: 500,
            ':hover': {
              color: themeColors.primary,
              bgcolor: themeColors.secondary,
              borderColor: themeColors.primary,
            },
            borderRadius: '4px',
          }}
        >
          Reset
        </Button>
      </Box>
      {/* Advanced Filters button */}
      <Box mt={3}>
        <Button
          fullWidth
          variant='outlined'
          onClick={() => {
            setShowAdvancedFilters(!showAdvancedFilters)
          }}
          endIcon={
            showAdvancedFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />
          }
          sx={{
            color: themeColors.primary,
            bgcolor: '#fff',
            borderColor: themeColors.border,
            fontWeight: 500,
            ':hover': {
              color: themeColors.primary,
              bgcolor: themeColors.secondary,
              borderColor: themeColors.primary,
            },
            borderRadius: '4px',
          }}
        >
          Advanced Filters
        </Button>
      </Box>
      {/* Advanced filters section - collapsed by default */}
      <Collapse in={showAdvancedFilters}>
        <Box mt={3}>
          <Divider sx={{ mb: 2 }} />
          <Typography
            variant='h6'
            sx={{
              // mb: 1,
              color: themeColors.primary,
              fontWeight: 500,
            }}
          >
            Advanced Filters
          </Typography>
          <Typography
            variant='caption'
            sx={{
              mb: 1,
              color: themeColors.primary,
              fontStyle: 'italic',
              lineHeight: 1.1,
              // fontWeight: 500,
            }}
          >
            * NOT Applicable on Law-Articles
          </Typography>
          {/* Year multi-select dropdown */}
          {/* {
            <Box sx={{ px: 2, mt: 2 }}>
              <Typography variant='body2' gutterBottom>
                Select Year Range
              </Typography>
              <Slider
                value={yearRange}
                onChange={(e, newValue) => setYearRange(newValue)}
                onChangeCommitted={(e, newValue) => {
                  // Trigger search with year range
                  const years = []
                  for (let i = newValue[0]; i <= newValue[1]; i++) {
                    years.push(i.toString())
                  }
                  setSelectedYears(years)
                  updateUrlParams(
                    selectedCountry,
                    selectedLawShortName,
                    searchQuery,
                    years,
                  )
                }}
                valueLabelDisplay='auto'
                min={2018}
                max={2025}
                marks={[
                  { value: 2018, label: '2018' },
                  { value: 2025, label: '2025' },
                ]}
                sx={{
                  color: themeColors.primary,
                  '& .MuiSlider-thumb': {
                    bgcolor: themeColors.secondary,
                  },
                }}
              />
            </Box>
          } */}
          {
            // tabValue !== 0 && ( // Hide for Articles tab (index 0)
            <CustomFormControl size='small' fullWidth margin='normal'>
              <InputLabel shrink={selectedYears.length > 0}>
                Select Years
              </InputLabel>
              <Select
                value={selectedYears}
                onChange={handleYearsChange}
                multiple
                input={<OutlinedInput label='Select Years' />}
                disabled={!selectedCountry || !selectedLawShortName || loading}
                renderValue={(selected) => selected.join(', ')}
                // renderValue={(selected) => (
                //   <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                //     {selected.map((value) => (
                //       <Chip
                //         key={value}
                //         label={value}
                //         size='small'
                //         sx={{
                //           bgcolor: themeColors.secondary,
                //           color: themeColors.primary,
                //           fontWeight: 500,
                //         }}
                //       />
                //     ))}
                //   </Box>
                // )}
                // MenuProps={MenuProps}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: themeColors.border,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: themeColors.primary,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: themeColors.primary,
                  },
                  fontSize: '0.875rem',
                }}
              >
                {AVAILABLE_YEARS.map((year) => (
                  <MenuItem key={year} value={year} dense>
                    <Checkbox
                      checked={selectedYears.indexOf(year) > -1}
                      size='small'
                      sx={{
                        color: themeColors.border,
                        '&.Mui-checked': {
                          color: themeColors.primary,
                        },
                      }}
                    />
                    <ListItemText
                      primary={year}
                      primaryTypographyProps={{ fontSize: '0.875rem' }}
                    />
                  </MenuItem>
                ))}
                Select{' '}
              </Select>
            </CustomFormControl>
            // )
          }
          {/* Universal search link */}
          {/* <Box mt={2} display='flex' justifyContent='start'>
            <Link
              href={universalSearchUrl}
              sx={{
                color: themeColors.primary,
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              <ArrowBackIcon fontSize='small' sx={{ mr: 0.5 }} />
              Back to Universal Search
            </Link>
          </Box> */}
        </Box>
      </Collapse>
    </Paper>
  )
}
/**
 * GlobalSearchInLawCSP component for searching across different content types
 */
const GlobalSearchInLawCSP = ({
  useExactSearch = false,
  autocompleteEnabled = true,
} = {}) => {
  // usePageTracking()
  const MemoizedPagination = React.memo(Pagination)
  const theme = useTheme()
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'))
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const {
    countriesWithLaws,
    refreshCountriesData: fetchCountriesWithLaws,
    selectedCountry,
    setSelectedCountry,
    availableLaws,
    setAvailableLaws,
  } = useCountryLaw()
  // const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // Get query parameters
  const countryParam = searchParams.get('country')
  const lawParam = searchParams.get('law')
  const queryParam = searchParams.get('query')
  const yearParam = searchParams.get('year')
  // State for filters
  // const [selectedCountry, setSelectedCountry] = useState(
  //   countryParam || selectedCountry || ''
  // )
  const [selectedLawShortName, setSelectedLawShortName] = useState(
    lawParam || '',
  )
  const [searchQuery, setSearchQuery] = useState(queryParam || '')
  const [selectedYears, setSelectedYears] = useState(
    yearParam
      ? yearParam.includes(',')
        ? yearParam.split(',')
        : [yearParam]
      : [],
  )
  // If search query chip to only appear after clicking search, add a temporary state and "hasSearched" flag.
  // const [hasSearched, setHasSearched] = useState(Boolean(queryParam))

  // Tabs state
  const [tabValue, setTabValue] = useState(0) // 0 = Articles, 1 = Decisions, 2 = Guides
  const [decisionTypeTabValue, setDecisionTypeTabValue] = useState(0)
  const [guidelineTypeTabValue, setGuidelineTypeTabValue] = useState(0)

  // Refs to prevent redundant API calls
  const searchRequestRef = useRef(null)
  const hasInitializedRef = useRef(false)
  const isUrlTriggeredRef = useRef(false)

  // const [pendingFilterChanges, setPendingFilterChanges] = useState(false)

  // Pagination state - separate for each content type
  const [currentArticlePage, setCurrentArticlePage] = useState(1)
  const [currentDecisionPage, setCurrentDecisionPage] = useState(1)
  const [currentFtaGuidePage, setCurrentFtaGuidePage] = useState(1)

  // Items per page
  const [itemsPerPage, setItemsPerPage] = useState(10)
  // Search results state
  const [results, setResults] = useState({
    articles: [],
    decisions: [],
    guidelines: [],
  })
  // const [filteredData, setFilteredData] = useState([]) // selected data for the current tab
  const { isAuthenticated, user } = useAuth()
  // Loading state
  const [loading, setLoading] = useState(false)
  // No-results modal state — opens after a search that returns zero hits
  // across v2 and v1 fallback, so the user can adjust the law/query.
  const [showNoResultsModal, setShowNoResultsModal] = useState(false)
  // Track which query/law the popup should display. We capture these at
  // search-response time so later typing in the sidebar doesn't mutate the
  // message for a modal that's already on screen.
  const [noResultsSnapshot, setNoResultsSnapshot] = useState({
    query: '',
    lawName: '',
  })

  // Resolve the user-facing law scope label for the no-results popup.
  // Uses the lawFullName if found (falling back to the short slug), and
  // prepends the country if not already baked into the name — so the user
  // sees e.g. "UAE CIT (FDL No 47 of 2022, as amended)" rather than just
  // "CIT (FDL No 47 of 2022, as amended)".
  const resolveLawScopeName = useCallback(
    (shortName, country) => {
      if (!shortName) return ''
      const match = (availableLaws || []).find(
        (law) =>
          (law.lawShortName || '').toLowerCase() === shortName.toLowerCase(),
      )
      const baseName = match?.lawFullName || shortName
      const trimmedCountry = (country || '').trim()
      if (!trimmedCountry) return baseName
      const startsWithCountry = baseName
        .toLowerCase()
        .startsWith(trimmedCountry.toLowerCase())
      return startsWithCountry ? baseName : `${trimmedCountry} ${baseName}`
    },
    [availableLaws],
  )
  // const [isInitializing, setIsInitializing] = useState(true)
  const [error, setError] = useState(null)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState('info')
  const handleShowSnackbar = useCallback((message, severity = 'info') => {
    setSnackbarMessage(message)
    setSnackbarSeverity(severity)
    setSnackbarOpen(true)
  }, [])
  // const [availableCountries, setAvailableCountries] = useState([])

  // Show/hide filters (for responsive design)
  const [showFilters, setShowFilters] = useState(!isSmallScreen)

  // Scroll to top state
  const [showBackToTop, setShowBackToTop] = useState(false)

  const lastFetchTimestamp = useRef(0)
  const debouncedSearchTimeout = useRef(null)
  const currentAbortController = useRef(null) //  timestamp-based approach to avoid processing outdated responses, but there's a potential issue in the implementation: This works, but doesn't account for network errors that might cause requests to complete out of order.
  const domainInfo = getDomainInfo()
  const showCountrySelector = domainInfo.shouldShowCountrySelector
  // Theme colors
  const themeColors = {
    primary: '#232536',
    secondary: '#ffcf51',
    hover: '#3a3e5a',
  }

  // Consistent button radius
  const buttonBorderRadius = '4px'

  // Update URL with search parameters
  const updateUrlParams = useCallback(
    (country, law, query, years = []) => {
      const params = new URLSearchParams()
      if (country) params.set('country', country)
      if (law) params.set('law', law)
      if (query) params.set('query', query)
      // Handle years parameter
      if (years.length === 1) {
        params.set('year', years[0])
      } else if (years.length > 1) {
        params.set('year', years.join(','))
      }
      //   navigate({
      //     pathname: '/search',
      //     search: params.toString(),
      //   })
      // },
      // [navigate]
      const nextSearch = params.toString()
      if (nextSearch === searchParams.toString()) {
        return
      }
      setSearchParams(params)
    },
    [searchParams, setSearchParams],
  )

  const decisionTypes = useMemo(() => {
    const allTypes = [
      'BL - Bylaws',
      'ERS - Executive Regulations',
      'IR - Implementing Regulations',
      'EB - Executive Bylaws',
      'CD - Cabinet Decision',
      'MD - Ministerial Decision',
      'FTA - Federal Tax Authority Decision',
      'AD - Administrative Decision',
      'ZD - ZATCA Decision',
      'ER - Executive Rules and Instructions',
      'JD - Judicial Decision',
    ]

    // Only return types that have data in the current results
    return allTypes.filter((type) => {
      const yearFilteredDecisions =
        selectedYears.length > 0
          ? results.decisions?.filter((item) =>
              selectedYears.includes(item.year?.toString()),
            )
          : results.decisions || []

      return yearFilteredDecisions.some((decision) => decision.type === type)
    })
  }, [results.decisions, selectedYears])
  const guidelineTypes = useMemo(() => {
    const allTypes = [
      'GUIDE - Federal Tax Authority Guide',
      'GUIDE - Federal Tax Authority Service Guide',
      'GUIDE - Zakat, Tax and Customs Authority',
      'GUIDE - Foreign Account Tax Compliance Act Guide',
      'GUIDE - VAT Taxpayer Guide',
      'GUIDE - Oman Tax Authority',
      'GUIDE - Qatar Tax Authority',
      'GUIDE - National Bureau for Revenue',
      'PC - Public Clarification',
      'CIRCULAR - Circular',
      'TBC - Tax Bulletin and Circular',
    ]

    // Only return types that have data in current results
    return allTypes.filter((type) => {
      const yearFilteredGuidelines =
        selectedYears.length > 0
          ? results.guidelines?.filter((item) =>
              selectedYears.includes(item.year?.toString()),
            )
          : results.guidelines || []

      return yearFilteredGuidelines.some((guide) => guide.type === type)
    })
  }, [results.guidelines, selectedYears])
  const contentCounts = useMemo(() => {
    const yearFilteredDecisions =
      selectedYears.length > 0
        ? results.decisions?.filter((item) =>
            selectedYears.includes(item.year?.toString()),
          )
        : results.decisions || []

    // Decision types that should display count as 1 instead of actual count
    const singleCountDecisionTypes = [
      'BL - Bylaws',
      'ERS - Executive Regulations',
      'IR - Implementing Regulations',
      'EB - Executive Bylaws',
    ]

    const decisionTypeCounts = {}
    let totalDecisionsAdjustment = 0

    decisionTypes.forEach((type) => {
      const actualCount = yearFilteredDecisions.filter(
        (d) => d.type === type,
      ).length

      if (singleCountDecisionTypes.includes(type)) {
        // Set count to 1 for these types
        decisionTypeCounts[type] = actualCount > 0 ? 1 : 0
        // Adjust total: subtract actual count, add 1 (if there are any items)
        if (actualCount > 0) {
          totalDecisionsAdjustment += 1 - actualCount
        }
      } else {
        decisionTypeCounts[type] = actualCount
      }
    })

    const allGuidelines = results.guidelines || []
    const yearFilteredGuidelines =
      selectedYears.length > 0
        ? allGuidelines.filter((item) =>
            selectedYears.includes(item.year?.toString()),
          )
        : allGuidelines

    // Guide types that should display count as 1 instead of actual count
    const singleCountGuideTypes = [
      // Add guide types here as needed
    ]

    // Create guideline type counts
    const guidelineTypeCounts = {}
    let totalGuidelinesAdjustment = 0

    guidelineTypes.forEach((type) => {
      const actualCount = yearFilteredGuidelines.filter(
        (g) => g.type === type,
      ).length

      if (singleCountGuideTypes.includes(type)) {
        // Set count to 1 for these types
        guidelineTypeCounts[type] = actualCount > 0 ? 1 : 0
        // Adjust total: subtract actual count, add 1 (if there are any items)
        if (actualCount > 0) {
          totalGuidelinesAdjustment += 1 - actualCount
        }
      } else {
        guidelineTypeCounts[type] = actualCount
      }
    })

    // Calculate adjusted decisions count
    const actualDecisionsCount = yearFilteredDecisions.length || 0
    const adjustedDecisionsCount =
      actualDecisionsCount + totalDecisionsAdjustment

    // Calculate adjusted guidelines count
    const actualGuidelinesCount = yearFilteredGuidelines.length || 0
    const adjustedGuidelinesCount =
      actualGuidelinesCount + totalGuidelinesAdjustment

    return {
      articles: results.articles?.length || 0,
      decisions: adjustedDecisionsCount,
      guidelines: adjustedGuidelinesCount,
      decisionTypeCounts,
      guidelineTypeCounts,
    }
  }, [results, decisionTypes, guidelineTypes, selectedYears])
  const availableTabs = useMemo(() => {
    const tabs = []

    if (contentCounts.articles > 0) {
      tabs.push({
        index: 0,
        key: 'articles',
        label: 'Law - Articles',
        count: contentCounts.articles,
      })
    }

    if (contentCounts.decisions > 0) {
      tabs.push({
        index: tabs.length,
        key: 'decisions',
        label: 'Decisions',
        count: contentCounts.decisions,
      })
    }

    if (contentCounts.guidelines > 0) {
      tabs.push({
        index: tabs.length,
        key: 'guidelines', // Changed from 'guides'
        label: 'Supplementary Publications',
        count: contentCounts.guidelines, // Combined count
      })
    }

    return tabs
  }, [contentCounts])
  // Handle tab change
  const handleTabChange = useCallback(
    (event, newValue) => {
      if (newValue >= 0 && newValue < availableTabs.length) {
        setTabValue(newValue)
        if (isAuthenticated) {
          const selectedTab = availableTabs[newValue]
          activityTracker.trackCustomEvent('search_tab_switched', {
            from: availableTabs[tabValue]?.label,
            to: selectedTab?.label,
            resultsCount: selectedTab?.count,
          })
        }
      }
    },
    [availableTabs, tabValue, isAuthenticated],
  )
  const handleDecisionTypeTabChange = (event, newValue) => {
    setDecisionTypeTabValue(newValue)
    // Reset pagination to page 1 when switching decision types
    setCurrentDecisionPage(1)

    if (isAuthenticated) {
      const selectedType = decisionTypes[newValue]
      activityTracker.trackCustomEvent('decision_type_filtered', {
        type: selectedType,
        resultsCount: contentCounts.decisionTypeCounts[selectedType],
      })
    }
  }

  const handleGuidelineTypeTabChange = (event, newValue) => {
    setGuidelineTypeTabValue(newValue)
    // Reset pagination to page 1 when switching guideline types
    setCurrentFtaGuidePage(1)
  }

  // const decisionTypes = useMemo(() => {
  //   const types = new Set()
  //   if (results.decisions) {
  //     results.decisions.forEach((decision) => {
  //       if (decision.type) types.add(decision.type)
  //     })
  //   }
  //   return [...types].sort()
  // }, [results.decisions])

  // Filter decisions by type
  const filteredDecisions = useMemo(() => {
    let filtered = [...(results.decisions || [])]

    // Filter by years if selected
    if (selectedYears.length > 0) {
      filtered = filtered.filter((item) =>
        selectedYears.includes(item.year?.toString()),
      )
    }

    // Filter by type based on the selected tab
    if (decisionTypes.length > 0) {
      const selectedType = decisionTypes[decisionTypeTabValue]
      filtered = filtered.filter((item) => item.type === selectedType)
    }

    return filtered
  }, [results.decisions, selectedYears, decisionTypeTabValue, decisionTypes])

  const filteredGuidelines = useMemo(() => {
    let filtered = [...(results.guidelines || [])]

    // Filter by years if selected
    if (selectedYears.length > 0) {
      filtered = filtered.filter((item) =>
        selectedYears.includes(item.year?.toString()),
      )
    }

    // Filter by type based on the selected tab
    if (guidelineTypes.length > 0) {
      const selectedType = guidelineTypes[guidelineTypeTabValue]
      filtered = filtered.filter((item) => item.type === selectedType)
    }

    return filtered
  }, [results.guidelines, selectedYears, guidelineTypeTabValue, guidelineTypes])

  // client side pagination logic
  const getPaginatedItems = (items, page, itemsPerPage) => {
    const startIndex = (page - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return items.slice(startIndex, endIndex)
  }

  // Toggle filters visibility (for mobile)
  const toggleFilters = useCallback(() => {
    setShowFilters((prev) => !prev)
  }, [])
  // Fetch search results for all content types
  // Simplify fetchSearchResults to just prepare the request and trigger loading
  const fetchSearchResults = useCallback(
    (directQuery = null) => {
      if (!selectedCountry || !selectedLawShortName) {
        return
      }

      // Clear any pending debounced search
      if (debouncedSearchTimeout.current) {
        clearTimeout(debouncedSearchTimeout.current)
        debouncedSearchTimeout.current = null
      }

      // Use direct query if provided, otherwise use state
      const queryToUse = directQuery !== null ? directQuery : searchQuery

      // Create a unique timestamp
      const searchTimestamp = Date.now()
      lastFetchTimestamp.current = searchTimestamp

      // Create search request
      const currentRequest = {
        country: selectedCountry,
        lawShortName: selectedLawShortName,
        query: queryToUse,
        // years: selectedYears,
        // articlePage: 1, // Reset to page 1
        // decisionPage: 1,
        // guidePage: 1,
        // pageSize: itemsPerPage,
        timestamp: searchTimestamp,
      }

      // Store request
      searchRequestRef.current = currentRequest

      // Reset pagination
      setCurrentArticlePage(1)
      setCurrentDecisionPage(1)
      setCurrentFtaGuidePage(1)

      // Start loading
      setLoading(true)
    },
    [selectedCountry, selectedLawShortName, searchQuery],
  )

  // Effect for when items per page changes
  // useEffect(() => {
  //   if (selectedCountry && selectedLawShortName && hasSearched) {
  //     // Reset pagination
  //     setCurrentArticlePage(1)
  //     setCurrentDecisionPage(1)
  //     setCurrentGuidePage(1)

  //     // Trigger search with new page size
  //     fetchSearchResults()
  //   }
  // }, [
  //   itemsPerPage,
  //   fetchSearchResults,
  //   selectedCountry,
  //   selectedLawShortName,
  //   hasSearched,
  // ])
  // Effect for when items per page changes from 10 items per page to 30 items per page
  useEffect(() => {
    if (selectedCountry && selectedLawShortName) {
      // Reset pagination to page 1 for all content types
      setCurrentArticlePage(1)
      setCurrentDecisionPage(1)
      setCurrentFtaGuidePage(1)

      // Create search request with new page size
      // const searchTimestamp = Date.now()
      // lastFetchTimestamp.current = searchTimestamp

      // const currentRequest = {
      //   country: selectedCountry,
      //   lawShortName: selectedLawShortName,
      //   query: searchQuery || '',
      //   years: selectedYears,
      //   // articlePage: 1, // Always reset to page 1 when changing items per page
      //   // decisionPage: 1,
      //   // guidePage: 1,
      //   // pageSize: itemsPerPage,
      //   timestamp: searchTimestamp,
      // }

      // // Store the request
      // searchRequestRef.current = currentRequest

      // // Trigger search with new page size
      // setLoading(true)
    }
  }, [itemsPerPage])
  // Trigger fetch when loading state changes
  // Remove the separate loading useEffect and integrate it directly
  useEffect(() => {
    if (loading && searchRequestRef.current) {
      const fetchData = async () => {
        try {
          const searchTimestamp = Date.now()
          lastFetchTimestamp.current = searchTimestamp

          const currentRequest = {
            ...searchRequestRef.current,
            timestamp: searchTimestamp,
          }

          const isComplexQuery = /[\[\]\(\){}^$<>]/.test(currentRequest.query)
          const encodedQuery = isComplexQuery
            ? btoa(unescape(encodeURIComponent(currentRequest.query)))
            : currentRequest.query

          // Make the API call - use V1 (exact-search) when caller opts in, otherwise V2
          const response = useExactSearch
            ? await apiService.globalSearchAllV1(
                {
                  country: currentRequest.country,
                  lawShortName: currentRequest.lawShortName,
                  query: encodedQuery,
                  isEncodedQuery: isComplexQuery ? 'true' : 'false',
                },
                {
                  cancelPrevious: true,
                  abortKey: `global-search-law-csp-v1:${currentRequest.country}:${currentRequest.lawShortName}`,
                },
              )
            : await apiService.globalSearchCSPV2(
                {
                  country: currentRequest.country,
                  lawShortName: currentRequest.lawShortName,
                  q: encodedQuery,
                  isEncodedQuery: isComplexQuery ? 'true' : 'false',
                },
                {
                  cancelPrevious: true,
                  abortKey: `global-search-law-csp-v2:${currentRequest.country}:${currentRequest.lawShortName}`,
                },
              )

          // Regular expression to check if string is Base64 encoded
          // const base64Regex = /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/

          // Base64 detection is not 100% reliable, as some valid strings can also match the Base64 pattern, so another simpler approach:
          // query: currentRequest.query                 <--- do this on frontend
          //   .replace(/\[/g, '__OPEN_BRACKET__')
          //   .replace(/\]/g, '__CLOSE_BRACKET__')
          //   .replace(/\(/g, '__OPEN_PAREN__')
          //   .replace(/\)/g, '__CLOSE_PAREN__')

          //   query = query                            <--- do this on backend
          //     .replace('__OPEN_BRACKET__', '[')
          //     .replace('__CLOSE_BRACKET__', ']')
          //     .replace('__OPEN_PAREN__', '(')
          //     .replace('__CLOSE_PAREN__', ')')

          // Check if this is still the current request
          if (searchTimestamp !== lastFetchTimestamp.current) {
            // console.log(
            //   `Skipping outdated response ${searchTimestamp} ${lastFetchTimestamp.current}`
            // )
            setLoading(false)
            return
          }

          // Store all results in a single state object for client-side filtering
          setResults({
            articles: response.articles || [],
            decisions: response.decisions || [],
            guidelines: response.guidelines || [],
          })

          const totalHits =
            (response.articles?.length || 0) +
            (response.decisions?.length || 0) +
            (response.guidelines?.length || 0)
          // Show the "no results" popup only after an authenticated user
          // actively searched with a query and got zero hits across v2 + v1.
          const shouldShowNoResults =
            totalHits === 0 &&
            Boolean(currentRequest.query) &&
            Boolean(isAuthenticated)
          if (shouldShowNoResults) {
            setNoResultsSnapshot({
              query: currentRequest.query || '',
              lawName: resolveLawScopeName(
                currentRequest.lawShortName,
                currentRequest.country,
              ),
            })
          }
          setShowNoResultsModal(shouldShowNoResults)

          if (isAuthenticated) {
            // if (isAuthenticated && currentRequest.query) { // if authenticated AND has query
            const articlesCount = response.articles?.length || 0
            const decisionsCount = response.decisions?.length || 0
            const guidelinesCount = response.guidelines?.length || 0
            const totalResults = articlesCount + decisionsCount + guidelinesCount
            // Only track if authenticated
            activityTracker.trackSearchInteraction(
              'global-search', // searchType
              currentRequest.query, // query
              {
                // filters
                country: currentRequest.country,
                law: currentRequest.lawShortName,
                years: selectedYears.length > 0 ? selectedYears : undefined,
              },
              totalResults,
              {
                articles: articlesCount,
                decisions: decisionsCount,
                guidelines: guidelinesCount,
              },
              {
                selectedSuggestion: currentRequest.selectedSuggestion,
              },
            )
          }

          // Reset pagination
          setCurrentArticlePage(1)
          setCurrentDecisionPage(1)
          setCurrentFtaGuidePage(1)

          const formattedYears =
            selectedYears.length > 0
              ? `for year${
                  selectedYears.length > 1 ? 's' : ''
                } ${selectedYears.join(', ')}`
              : ''
          const formattedQuery = currentRequest.query
            ? `with query "${currentRequest.query}"`
            : ''

          let message = 'Search completed successfully'

          let severity = 'success'
          if (currentRequest.query) {
            // Show search success message only if there was a query
            if (formattedYears && formattedQuery) {
              message = `Search ${formattedQuery} ${formattedYears} completed successfully`
            } else if (formattedQuery) {
              message = `Search ${formattedQuery} completed successfully`
            } else if (formattedYears) {
              message = `Search ${formattedYears} completed successfully`
            }
          } else {
            // For empty queries (like reset), show different message
            message = 'Filters cleared successfully'
            severity = 'info'
          }

          setSnackbarMessage(message)
          setSnackbarSeverity(severity)
          setSnackbarOpen(true)
        } catch (error) {
          if (apiService.isRequestCanceled(error) || error?.isCanceled) {
            return
          }
          console.error('Error fetching search results:', error)
          const errorMessage =
            error && error.userMessage
              ? error.userMessage
              : 'Failed to load search results. Please try again.'

          setError(errorMessage)
          setSnackbarMessage(errorMessage)
          setSnackbarSeverity('error')
          setSnackbarOpen(true)
        } finally {
          setLoading(false)
        }
      }

      fetchData()
    }
  }, [
    loading,
    isAuthenticated,
    selectedYears,
    useExactSearch,
    // , tabValue
  ])

  // Effect to handle tab selection based on available results
  useEffect(() => {
    if (!loading && availableTabs.length > 0) {
      // If current tab value is out of bounds, reset to 0
      if (tabValue >= availableTabs.length) {
        setTabValue(0)
      }
    } else if (!loading && availableTabs.length === 0) {
      // If no tabs available, reset tabValue
      setTabValue(0)
    }
  }, [loading, availableTabs])
  // Effect to handle decision type tab selection based on available results
  // useEffect(() => {
  //   if (!loading && decisionTypes.length > 0) {
  //     // Determine which decision type tab should be active based on available data
  //     const selectAppropriateDecisionTypeTab = () => {
  //       // If the current tab has data, keep it selected
  //       if (
  //         decisionTypeTabValue >= 0 &&
  //         decisionTypeTabValue < decisionTypes.length &&
  //         contentCounts.decisionTypeCounts[
  //           decisionTypes[decisionTypeTabValue]
  //         ] > 0
  //       ) {
  //         return decisionTypeTabValue
  //       }

  //       // Otherwise, find the first tab with data
  //       for (let i = 0; i < decisionTypes.length; i++) {
  //         if (contentCounts.decisionTypeCounts[decisionTypes[i]] > 0) {
  //           return i
  //         }
  //       }

  //       // If no tab has data, default to first tab
  //       return 0
  //     }

  //     const newDecisionTypeTabValue = selectAppropriateDecisionTypeTab()
  //     if (newDecisionTypeTabValue !== decisionTypeTabValue) {
  //       setDecisionTypeTabValue(newDecisionTypeTabValue)
  //     }
  //   }
  // }, [
  //   loading,
  //   decisionTypes,
  //   contentCounts.decisionTypeCounts,
  //   decisionTypeTabValue,
  // ])
  useEffect(() => {
    if (!loading && decisionTypes.length > 0) {
      // Check if current tab index is valid for the new decisionTypes array
      if (decisionTypeTabValue >= decisionTypes.length) {
        setDecisionTypeTabValue(0) // Reset to first available tab
        setCurrentDecisionPage(1) // Reset pagination when tab is reset
      }
    } else if (!loading && decisionTypes.length === 0) {
      setDecisionTypeTabValue(0)
      setCurrentDecisionPage(1) // Reset pagination when tab is reset
    }
  }, [loading, decisionTypes])

  useEffect(() => {
    if (!loading && guidelineTypes.length > 0) {
      // Check if current tab index is valid for the new guidelineTypes array
      if (guidelineTypeTabValue >= guidelineTypes.length) {
        setGuidelineTypeTabValue(0) // Reset to first available tab
        setCurrentFtaGuidePage(1) // Reset pagination when tab is reset
      }
    } else if (!loading && guidelineTypes.length === 0) {
      setGuidelineTypeTabValue(0)
      setCurrentFtaGuidePage(1) // Reset pagination when tab is reset
    }
  }, [loading, guidelineTypes, guidelineTypeTabValue])

  // Initialize search from URL parameters
  useEffect(() => {
    // Get search parameters from URL
    const country = searchParams.get('country')
    const law = searchParams.get('law')
    const query = searchParams.get('query') || ''
    const year = searchParams.get('year') || ''
    // `fromSuggestion` is set by the homepage HeroSection's LexSearch box
    // when the user submitted by clicking an autosuggestion (rather than
    // typing). Propagates into the search_interaction activity so the
    // public Trending Searches feed can count this hit. Only honoured when
    // it matches the typed query — guards against an old/stale value on
    // back-navigation. Page-internal autocomplete clicks bypass the URL
    // entirely and feed the same field via SearchSideBar state instead.
    const fromSuggestionParam = searchParams.get('fromSuggestion')
    const fromSuggestion =
      typeof fromSuggestionParam === 'string'
        && fromSuggestionParam.trim().length > 0
        && fromSuggestionParam.trim() === query.trim()
        ? fromSuggestionParam.trim()
        : null
    // For country-specific domains, auto-select the country if not already set
    if (
      domainInfo.isCountrySpecific &&
      !selectedCountry &&
      countriesWithLaws.length > 0
    ) {
      const countryFromDomain = getCountryFromDomain()
      if (countryFromDomain) {
        isUrlTriggeredRef.current = true
        setSelectedCountry(countryFromDomain)
      }
    }
    // Only consider URL parameters valid if both country and law are specified
    const hasValidUrlParams = country && law
    // Track if we're updating from URL to prevent double triggers
    let shouldTriggerSearch = false

    // Update state with URL parameters
    if (country && country !== selectedCountry) {
      isUrlTriggeredRef.current = true
      // Find the actual country data to get the correct name format, handle lowercase properly for e.g. Kuwait UAE uae
      const countryData = countriesWithLaws.find(
        (c) =>
          c.countryName.toLowerCase() === country.toLowerCase() ||
          c.slug?.toLowerCase() === country.toLowerCase(),
      )
      const normalizedCountry = countryData ? countryData.countryName : country
      setSelectedCountry(normalizedCountry)
      shouldTriggerSearch = hasValidUrlParams
    }
    if (law && law !== selectedLawShortName) {
      isUrlTriggeredRef.current = true
      setSelectedLawShortName(law)
      shouldTriggerSearch = hasValidUrlParams
    } else if (!law && selectedLawShortName) {
      isUrlTriggeredRef.current = true
      setSelectedLawShortName('')
    }

    if (query !== searchQuery) {
      setSearchQuery(query)
      shouldTriggerSearch = hasValidUrlParams
    }
    if (year !== selectedYears.join(',')) {
      const newYears = year
        ? year.includes(',')
          ? year.split(',')
          : [year]
        : []
      setSelectedYears(newYears)
      shouldTriggerSearch = hasValidUrlParams
    }
    // We need to check if this is not the initial load AND if parameters changed
    if (hasInitializedRef.current && shouldTriggerSearch) {
      // Create a new search request based on updated URL params
      const searchRequest = {
        country: country || selectedCountry,
        lawShortName: law || selectedLawShortName,
        query: query || '',
        // Propagated from the homepage hero (or any other caller that
        // appends &fromSuggestion=...) so the resulting search_interaction
        // is attributed as a suggestion-click for Trending Searches.
        selectedSuggestion: fromSuggestion,
        // years: year ? (year.includes(',') ? year.split(',') : [year]) : [],
        // articlePage: 1,
        // decisionPage: 1,
        // guidePage: 1,
        // pageSize: itemsPerPage,
      }

      // Reset pagination
      setCurrentArticlePage(1)
      setCurrentDecisionPage(1)
      setCurrentFtaGuidePage(1)

      // Store request
      searchRequestRef.current = searchRequest

      // Trigger a search
      setLoading(true)
    }
    // Always fetch on initial load when URL has required params.
    // Even if state already matches URL (common when navigating from homepage),
    // we still need to trigger the first data load.
    else if (country && law && !hasInitializedRef.current) {
      hasInitializedRef.current = true

      const initialRequest = {
        country,
        lawShortName: law,
        query: query || '',
        selectedSuggestion: fromSuggestion,
        // years: year ? (year.includes(',') ? year.split(',') : [year]) : [],
        // articlePage: 1,
        // decisionPage: 1,
        // guidePage: 1,
        // pageSize: itemsPerPage,
      }

      // Store request
      searchRequestRef.current = initialRequest

      // Trigger a search
      setLoading(true)
    }
  }, [searchParams, countriesWithLaws, domainInfo.isCountrySpecific])

  // Handle search button click
  const handleSearch = useCallback(
    (directQuery = null, selectedSuggestion = null) => {
      if (!selectedCountry || !selectedLawShortName) return

      // Get the query to use - prioritize direct query parameter
      const queryToUse = directQuery !== null ? directQuery : searchQuery
      const normalizedSelectedSuggestion =
        typeof selectedSuggestion === 'string' &&
        selectedSuggestion.trim().length > 0
          ? selectedSuggestion.trim()
          : null

      // Update URL parameters first
      updateUrlParams(
        selectedCountry,
        selectedLawShortName,
        queryToUse,
        selectedYears,
      )

      // Reset pagination
      setCurrentArticlePage(1)
      setCurrentDecisionPage(1)
      setCurrentFtaGuidePage(1)

      // Create search request
      const currentRequest = {
        country: selectedCountry,
        lawShortName: selectedLawShortName,
        query: queryToUse,
        selectedSuggestion: normalizedSelectedSuggestion,
        // years: selectedYears,
        // articlePage: 1,
        // decisionPage: 1,
        // guidePage: 1,
        // pageSize: itemsPerPage,
      }

      // Store the request
      searchRequestRef.current = currentRequest
      setResults({ articles: [], decisions: [], guidelines: [] })

      // Start loading
      setLoading(true)
    },
    [
      selectedCountry,
      selectedLawShortName,
      searchQuery,
      selectedYears,
      updateUrlParams,
      // itemsPerPage,
    ],
  )

  // Handle reset button click
  const handleReset = useCallback(() => {
    // Reset search query and filters
    setSearchQuery('')
    setSelectedYears([])
    setDecisionTypeTabValue(0) // Reset to "All Types"
    setGuidelineTypeTabValue(0)

    // Update URL with empty query
    updateUrlParams(selectedCountry, selectedLawShortName, '', [])

    // Reset pagination
    setCurrentArticlePage(1)
    setCurrentDecisionPage(1)
    setCurrentFtaGuidePage(1)

    // Create search request
    const currentRequest = {
      country: selectedCountry,
      lawShortName: selectedLawShortName,
      query: '', // Empty query
      // years: [],
      // articlePage: 1,
      // decisionPage: 1,
      // guidePage: 1,
      // pageSize: itemsPerPage,
    }

    // Store request
    searchRequestRef.current = currentRequest

    // Trigger new search
    setLoading(true)

    // Show message
    setSnackbarMessage('Filters have been reset')
    setSnackbarSeverity('warning') // this is getting override by useEffect when loading state variable gets updated, so we are not seeing yellow color snackbar
    setSnackbarOpen(true)
  }, [updateUrlParams, selectedCountry, selectedLawShortName])
  // Handle snackbar close
  const handleSnackbarClose = useCallback((event, reason) => {
    if (reason === 'clickaway') return
    setSnackbarOpen(false)
  }, [])
  // Handle scroll to top
  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }, [])
  // Show/hide back to top button based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 50)
    }

    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  // Render decision type tabs
  const renderDecisionTypeTabs = () => {
    // Don't render tabs if there are no decision types with data
    if (!decisionTypes.length) return null

    // Get the color scheme for the currently selected tab
    const selectedType = decisionTypes[decisionTypeTabValue]
    const selectedColorScheme =
      decisionTypeColors[selectedType] || decisionTypeColors.default

    return (
      <Tabs
        value={decisionTypeTabValue}
        onChange={handleDecisionTypeTabChange}
        variant='scrollable'
        scrollButtons='auto'
        aria-label='decision type tabs'
        sx={{
          backgroundColor: 'white',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          borderRadius: '4px',
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 500,
            minHeight: '48px',
            padding: '6px 16px',
            '&.Mui-selected': {
              fontWeight: 700, // Increased from 600 to 700 to make it more bold
            },
          },
          '& .MuiTabs-indicator': {
            height: 3,
            backgroundColor: selectedColorScheme.main, // Use the selected tab's color for the indicator
          },
        }}
      >
        {decisionTypes.map((type) => {
          // Get color scheme for this decision type or use default
          const colorScheme =
            decisionTypeColors[type] || decisionTypeColors.default

          return (
            <Tab
              key={type}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    sx={{
                      fontWeight:
                        decisionTypeTabValue === decisionTypes.indexOf(type)
                          ? 700
                          : 500, // Make text bold if selected
                    }}
                  >
                    {type.split('-')[1] || type}
                  </Typography>
                  <Chip
                    label={contentCounts.decisionTypeCounts[type]}
                    size='small'
                    sx={{
                      ml: 1,
                      height: 24,
                      fontSize: '0.75rem',
                      backgroundColor: colorScheme.light,
                      color: colorScheme.main,
                      fontWeight: 'bold',
                      border: `1px solid ${colorScheme.main}`,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: colorScheme.main,
                        color: 'white',
                      },
                    }}
                  />
                </Box>
              }
              // disabled={contentCounts.decisionTypeCounts[type] === 0}
              sx={{
                color: colorScheme.main,
                opacity: 0.7,
                '&.Mui-selected': {
                  color: colorScheme.main,
                  opacity: 1,
                },
              }}
            />
          )
        })}
      </Tabs>
    )
  }
  const renderGuidelineTypeTabs = () => {
    if (!guidelineTypes.length) return null

    const selectedType = guidelineTypes[guidelineTypeTabValue]
    const selectedColorScheme =
      guidelineTypeColors[selectedType] || guidelineTypeColors.default

    return (
      <Tabs
        value={guidelineTypeTabValue}
        onChange={handleGuidelineTypeTabChange}
        variant='scrollable'
        scrollButtons='auto'
        aria-label='guideline type tabs'
        sx={{
          backgroundColor: 'white',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          borderRadius: '4px',
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 500,
            minHeight: '48px',
            padding: '6px 16px',
            '&.Mui-selected': {
              fontWeight: 700,
            },
          },
          '& .MuiTabs-indicator': {
            height: 3,
            backgroundColor: selectedColorScheme.main, // Dynamic color
          },
        }}
      >
        {guidelineTypes.map((type) => {
          const colorScheme =
            guidelineTypeColors[type] || guidelineTypeColors.default

          return (
            <Tab
              key={type}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    sx={{
                      fontWeight:
                        guidelineTypeTabValue === guidelineTypes.indexOf(type)
                          ? 700
                          : 500,
                    }}
                  >
                    {type.split(' - ')[1] || type}
                  </Typography>
                  <Chip
                    label={contentCounts.guidelineTypeCounts[type]}
                    size='small'
                    sx={{
                      ml: 1,
                      height: 24,
                      fontSize: '0.75rem',
                      backgroundColor: colorScheme.light,
                      color: colorScheme.main,
                      fontWeight: 'bold',
                      border: `1px solid ${colorScheme.main}`,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: colorScheme.main,
                        color: 'white',
                      },
                    }}
                  />
                </Box>
              }
              sx={{
                color: colorScheme.main,
                opacity: 0.7,
                '&.Mui-selected': {
                  color: colorScheme.main,
                  opacity: 1,
                },
              }}
            />
          )
        })}
      </Tabs>
    )
  }
  /**
   * Defensive client-side sort that mirrors the backend's per-category
   * comparators in SearchV2Service.sortResults(...). Backend already applies
   * the same ordering, but we re-run it on the client so:
   *   1. The order stays consistent if the response shape ever changes
   *      (e.g. cache or fallback path that doesn't go through sortResults).
   *   2. Result reordering after client-side filtering (year + type) is
   *      still deterministic.
   *
   * articles: score DESC → orderIndex ASC → number ASC (numeric, not lex)
   */
  const sortedArticles = useMemo(() => {
    const articles = [...(results.articles || [])]
    articles.sort((a, b) => {
      const scoreA = a.score ?? 0
      const scoreB = b.score ?? 0
      if (scoreA !== scoreB) return scoreB - scoreA

      // orderIndex is the in-law position. Ascending. Missing → push to end.
      const orderA = Number.isFinite(Number(a.orderIndex))
        ? Number(a.orderIndex)
        : Number.POSITIVE_INFINITY
      const orderB = Number.isFinite(Number(b.orderIndex))
        ? Number(b.orderIndex)
        : Number.POSITIVE_INFINITY
      if (orderA !== orderB) return orderA - orderB

      // Numeric, not lexicographic. "100" must sort after "21".
      return parseLeadingNumeric(a.number) - parseLeadingNumeric(b.number)
    })
    return articles
  }, [results.articles])

  const renderArticleResults = useCallback(() => {
    // Year filter not applicable on articles (the article model has no
    // standalone year — it's part of the parent law). sortedArticles
    // applies the score/orderIndex/number ordering.
    return sortedArticles.length === 0 ? (
      <Typography>No articles found.</Typography>
    ) : (
      <>
        {getPaginatedItems(
          sortedArticles,
          currentArticlePage,
          itemsPerPage,
        ).map((article) => (
          <ArticleCard
            key={article.slug || article.id}
            article={article}
            searchQuery={searchQuery}
            showSnackbar={handleShowSnackbar}
          />
        ))}

        {/* Client-side pagination */}
        {sortedArticles.length > itemsPerPage && (
          <Box sx={{ mt: 3 }}>
            <MemoizedPagination
              itemsPerPage={itemsPerPage}
              totalItems={sortedArticles.length}
              currentPage={currentArticlePage}
              paginate={setCurrentArticlePage}
            />
          </Box>
        )}
      </>
    )
  }, [sortedArticles, currentArticlePage, itemsPerPage, searchQuery])

  /**
   * decisions: score DESC → year DESC → number DESC (numeric).
   * Prefer the structured `number` field when present, else parse "No. X"
   * out of `name`. Kuwait KIT TD/ER/BL/ERS/IR/EB types skip the client
   * sort because their naming pattern doesn't carry a parseable
   * year/number pair (matches backend skip in SearchV2Service).
   */
  const sortedDecisions = useMemo(() => {
    const decisions = [...filteredDecisions]

    const shouldSkipClientSort = decisions.some(
      (d) =>
        d.slug?.startsWith(
          'kuwait-kit-law-no-2-of-2008-amending-decree-no-3-of-1955',
        ) &&
        [
          'ER - Executive Rules and Instructions',
          'BL - Bylaws',
          'ERS - Executive Regulations',
          'IR - Implementing Regulations',
          'EB - Executive Bylaws',
        ].includes(d.type),
    )
    if (shouldSkipClientSort) return decisions

    const decisionNumber = (d) => {
      if (d?.number && String(d.number).trim()) {
        return parseLeadingNumeric(d.number)
      }
      const m = d?.name?.match(/No\.\s*(\d+)/)
      return m ? parseInt(m[1], 10) : 0
    }

    decisions.sort((a, b) => {
      const scoreA = a.score ?? 0
      const scoreB = b.score ?? 0
      if (scoreA !== scoreB) return scoreB - scoreA

      const yearA = Number(a.year) || 0
      const yearB = Number(b.year) || 0
      if (yearA !== yearB) return yearB - yearA

      return decisionNumber(b) - decisionNumber(a)
    })

    return decisions
  }, [filteredDecisions])

  /**
   * guidelines: score DESC → year DESC.
   * Year ties keep backend order (stable Array#sort).
   */
  const sortedGuidelines = useMemo(() => {
    const guidelines = [...filteredGuidelines]
    guidelines.sort((a, b) => {
      const scoreA = a.score ?? 0
      const scoreB = b.score ?? 0
      if (scoreA !== scoreB) return scoreB - scoreA

      const yearA = Number(a.year) || 0
      const yearB = Number(b.year) || 0
      return yearB - yearA
    })
    return guidelines
  }, [filteredGuidelines])
  // Render guide results
  const renderDecisionResults = useCallback(() => {
    return sortedDecisions.length === 0 ? (
      <Typography>No decisions found.</Typography>
    ) : (
      <>
        {getPaginatedItems(
          sortedDecisions,
          currentDecisionPage,
          itemsPerPage,
        ).map((decision) => (
          <DecisionCard
            key={decision.slug || decision.id}
            article={decision}
            searchQuery={searchQuery}
            showSnackbar={handleShowSnackbar}
          />
        ))}

        {/* Client-side pagination */}
        {sortedDecisions.length > itemsPerPage && (
          <Box sx={{ mt: 3 }}>
            <MemoizedPagination
              itemsPerPage={itemsPerPage}
              totalItems={sortedDecisions.length}
              currentPage={currentDecisionPage}
              paginate={setCurrentDecisionPage}
            />
          </Box>
        )}
      </>
    )
  }, [currentDecisionPage, sortedDecisions, itemsPerPage, searchQuery])
  const renderGuidelineResults = useCallback(() => {
    return sortedGuidelines.length === 0 ? (
      <Typography>No supplementary publications found.</Typography>
    ) : (
      <>
        {getPaginatedItems(
          sortedGuidelines,
          currentFtaGuidePage,
          itemsPerPage,
        ).map((guide) => (
          <GuideCard
            key={guide.slug || guide.id}
            article={guide}
            searchQuery={searchQuery}
            showSnackbar={handleShowSnackbar}
          />
        ))}

        {sortedGuidelines.length > itemsPerPage && (
          <Box sx={{ mt: 3 }}>
            <MemoizedPagination
              itemsPerPage={itemsPerPage}
              totalItems={sortedGuidelines.length}
              currentPage={currentFtaGuidePage}
              paginate={setCurrentFtaGuidePage}
            />
          </Box>
        )}
      </>
    )
  }, [sortedGuidelines, itemsPerPage, currentFtaGuidePage, searchQuery])

  // Render guide results

  // Render active filters
  const renderActiveFilters = useCallback(() => {
    if (!selectedCountry && !selectedLawShortName && !searchQuery) return null

    return (
      <Box sx={{ mt: 2, mb: 3 }}>
        <Typography variant='subtitle1' sx={{ mb: 1, fontWeight: 500 }}>
          Active Filters:
        </Typography>
        <Stack direction='row' spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          {selectedCountry && (
            <Chip
              label={`Country: ${selectedCountry}`}
              color='default'
              size='medium'
            />
          )}
          {selectedLawShortName && (
            <Chip
              label={`Law: ${selectedLawShortName // use selectedLawFullName
                .replace(/-/g, ' ')
                .toUpperCase()}`}
              color='default'
              size='medium'
            />
          )}
          {/* Year chips with delete functionality */}
          {selectedYears.map((year) => (
            <Chip
              key={year}
              label={`Year: ${year}`}
              color='secondary'
              size='medium'
              onDelete={() => {
                const newYears = selectedYears.filter((y) => y !== year)
                setSelectedYears(newYears)
                updateUrlParams(
                  selectedCountry,
                  selectedLawShortName,
                  searchQuery,
                  newYears,
                )

                // Trigger a new search with updated years
                // const searchTimestamp = Date.now()
                // lastFetchTimestamp.current = searchTimestamp

                // const currentRequest = {
                //   country: selectedCountry,
                //   lawShortName: selectedLawShortName,
                //   query: searchQuery,
                //   // years: newYears,
                //   // articlePage: 1,
                //   // decisionPage: 1,
                //   // guidePage: 1,
                //   // pageSize: itemsPerPage,
                //   timestamp: searchTimestamp,
                // }

                // searchRequestRef.current = currentRequest
                // setLoading(true)
              }}
              sx={{
                bgcolor: themeColors.secondary,
                color: themeColors.primary,
                '& .MuiChip-deleteIcon': {
                  color: 'rgba(35, 37, 54, 0.7)',
                  '&:hover': { color: themeColors.primary },
                },
              }}
            />
          ))}
          {searchQuery && (
            <Chip
              label={`Search: "${searchQuery}"`}
              color='success'
              size='medium'
              onDelete={() => {
                setSearchQuery('')
                updateUrlParams(
                  selectedCountry,
                  selectedLawShortName,
                  '',
                  selectedYears,
                )

                // Create a search request with empty query
                const searchTimestamp = Date.now()
                lastFetchTimestamp.current = searchTimestamp

                const currentRequest = {
                  country: selectedCountry,
                  lawShortName: selectedLawShortName,
                  query: '', // Empty query
                  years: selectedYears,
                  // articlePage: 1,
                  // decisionPage: 1,
                  // guidePage: 1,
                  // pageSize: itemsPerPage,
                  timestamp: searchTimestamp,
                }

                // Store request
                searchRequestRef.current = currentRequest

                // Reset pagination
                setCurrentArticlePage(1)
                setCurrentDecisionPage(1)
                setCurrentFtaGuidePage(1)
                // Trigger new search
                setLoading(true)
              }}
              sx={{
                '& .MuiChip-deleteIcon': {
                  color: 'rgba(0, 0, 0, 0.6)',
                  '&:hover': { color: 'rgba(0, 0, 0, 0.8)' },
                },
              }}
            />
          )}
        </Stack>
      </Box>
    )
  }, [
    selectedCountry,
    selectedLawShortName,
    searchQuery,
    selectedYears,
    updateUrlParams,
    themeColors,
  ])

  return (
    <Container maxWidth='lg' sx={{ py: 3 }}>
      {/* Breadcrumbs navigation */}
      <Breadcrumbs
        aria-label='breadcrumb'
        sx={{ mb: 2 }}
        separator={<NavigateNextIcon fontSize='small' />}
      >
        <Link
          color='inherit'
          href='/'
          sx={{
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          Home
        </Link>
        {/* <Link
          color='inherit'
          // searchQuery has stale data and "tempSearchQuery" state variable defined in SearchSideBar is passed to GlobalSearchInLawCSP component via handleSearch or fetchSearchResults method
          href={`/search-across-country?country=${selectedCountry}&query=${encodeURIComponent(
            searchQuery || ''
          )}`}
          sx={{
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          Search across country
        </Link> */}
        <Typography color='text.primary'>Search across Tax Law</Typography>
      </Breadcrumbs>
      <Grid container spacing={3}>
        {isMediumScreen ? (
          // Mobile layout - collapsible sidebar
          <>
            {/* Filter toggle button for mobile */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between', // 'flex-end'
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                {/* <Typography variant='h5' component='h1'>
                Search Results
              </Typography> */}
                <Button
                  variant='contained'
                  color='primary'
                  startIcon={
                    showFilters ? <FilterAltOffIcon /> : <FilterAltIcon />
                  }
                  onClick={toggleFilters}
                  sx={{
                    bgcolor: themeColors.primary,
                    color: '#fff',
                    '&:hover': {
                      bgcolor: themeColors.hover,
                    },
                    borderRadius: buttonBorderRadius,
                  }}
                >
                  {showFilters ? 'Hide Filters' : 'Show Filters'}
                </Button>
              </Box>
            </Grid>

            {/* Collapsible sidebar */}
            {showFilters && (
              <Grid item xs={12} sx={{ mb: 3 }}>
                <Paper elevation={2} sx={{ borderRadius: '8px' }}>
                  <SearchSideBar
                    selectedCountry={selectedCountry}
                    setSelectedCountry={setSelectedCountry}
                    selectedLawShortName={selectedLawShortName}
                    setSelectedLawShortName={setSelectedLawShortName}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    selectedYears={selectedYears}
                    setSelectedYears={setSelectedYears}
                    handleSearch={handleSearch}
                    handleReset={handleReset}
                    loading={loading}
                    updateUrlParams={updateUrlParams}
                    setCurrentArticlePage={setCurrentArticlePage}
                    setCurrentDecisionPage={setCurrentDecisionPage}
                    setCurrentFtaGuidePage={setCurrentFtaGuidePage}
                    setResults={setResults}
                    lastFetchTimestamp={lastFetchTimestamp}
                    itemsPerPage={itemsPerPage}
                    searchRequestRef={searchRequestRef}
                    setLoading={setLoading}
                    hasInitializedRef={hasInitializedRef}
                    isUrlTriggeredRef={isUrlTriggeredRef}
                    isAuthenticated={isAuthenticated}
                    autocompleteEnabled={autocompleteEnabled}
                  />
                </Paper>
              </Grid>
            )}

            {/* Active filters display */}
            <Grid item xs={12}>
              {renderActiveFilters()}
            </Grid>

            {/* Content */}
            <Grid item xs={12}>
              <Box sx={{ position: 'relative', minHeight: '200px' }}>
                {/* Loading overlay */}
                {loading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(255,255,255,0.7)',
                      zIndex: 5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CircularProgress
                      size={40}
                      sx={{ color: themeColors.primary }}
                    />
                  </Box>
                )}
                {error && (
                  <Alert severity='error' sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}
                {/* Empty state message */}
                {!loading &&
                  !error &&
                  results.articles.length === 0 &&
                  results.decisions.length === 0 &&
                  results.guidelines.length === 0 && (
                    <Alert severity='info' sx={{ mb: 2 }}>
                      {selectedCountry && selectedLawShortName
                        ? 'No results found. Try adjusting your filters.'
                        : 'Please select a country and law to search.'}
                    </Alert>
                  )}

                {/* Results content */}
                {(results.articles.length > 0 ||
                  results.decisions.length > 0 ||
                  results.guidelines.length > 0) && (
                  <Paper sx={{ width: '100%', mb: 3 }}>
                    <Tabs
                      value={tabValue}
                      onChange={handleTabChange}
                      variant='scrollable' // 'fullWidth'
                      scrollButtons='auto'
                      aria-label='search results tabs'
                      sx={{
                        backgroundColor: '#f5f5f5',
                        // background:
                        //   'linear-gradient(90deg, #f8f9fa 0%, #ffffff 100%)',
                        borderRadius: '8px 8px 0 0',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.03)',
                        position: 'relative',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: '3px',
                          background:
                            'linear-gradient(90deg, #e1e5eb 0%, #f8f9fa 100%)',
                          zIndex: 0,
                        },
                        '& .MuiTabs-flexContainer': {
                          justifyContent: 'flex-start', // Left align tabs
                          gap: '24px', // Add spacing between tabs
                          paddingLeft: '16px',
                        },
                        '& .MuiTab-root': {
                          textTransform: 'none',
                          fontWeight: 500,
                          minWidth: 'auto', // Change from 100 to auto
                          padding: '12px 16px', // Add more internal padding
                          // flex: 1, // to distribute tabs evenly
                          // maxWidth: 'none', // to allow tabs to expand
                          transition: 'all 0.3s ease',
                          position: 'relative',
                          overflow: 'hidden',
                          '&:hover': {
                            color: `${themeColors.primary}`,
                            background: 'rgba(248, 249, 250, 0.5)',
                          },
                        },
                        '& .Mui-selected': {
                          color: `${themeColors.primary} !important`,
                          fontWeight: 600,
                          background: 'rgba(248, 249, 250, 0.8)',
                        },
                        '& .MuiTabs-indicator': {
                          backgroundColor: themeColors.primary,
                          height: 3,
                          borderRadius: '3px 3px 0 0',
                          zIndex: 1,
                          // background: `linear-gradient(90deg, ${themeColors.primary} 0%, ${themeColors.secondary} 100%)`,
                        },
                      }}
                    >
                      {availableTabs.map((tab, index) => (
                        <Tab
                          key={tab.key}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography>{tab.label}</Typography>
                              <Chip
                                label={tab.count}
                                size='small'
                                sx={{
                                  ml: 1,
                                  height: 24,
                                  fontSize: '0.75rem',
                                  background: `linear-gradient(135deg, ${themeColors.primary} 0%, #3a3e5a 100%)`,
                                  color: 'white',
                                  fontWeight: 'bold',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    transform: 'scale(1.05)',
                                  },
                                }}
                              />
                            </Box>
                          }
                        />
                      ))}
                    </Tabs>

                    <Box
                      sx={{
                        backgroundColor: 'white',
                        borderRadius: '0 0 8px 8px',
                        p: 2,
                        // position: 'relative',
                        // minHeight: '200px',
                      }}
                    >
                      {availableTabs.map((tab, index) => (
                        <TabPanel key={tab.key} value={tabValue} index={index}>
                          {tab.key === 'articles' && renderArticleResults()}
                          {tab.key === 'decisions' && (
                            <>
                              {renderDecisionTypeTabs()}
                              {renderDecisionResults()}
                            </>
                          )}
                          {tab.key === 'guidelines' && (
                            <>
                              {renderGuidelineTypeTabs()}
                              {renderGuidelineResults()}
                            </>
                          )}
                        </TabPanel>
                      ))}
                    </Box>
                  </Paper>
                )}
                {/* Items per page selector */}
                {(results.articles.length > 0 ||
                  results.decisions.length > 0 ||
                  results.guidelines.length > 0) && (
                  <Box
                    sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}
                  >
                    <ItemsPerPageSelector
                      itemsPerPage={itemsPerPage}
                      setItemsPerPage={setItemsPerPage}
                      loading={loading}
                      themeColors={themeColors}
                    />
                  </Box>
                )}
              </Box>
            </Grid>
          </>
        ) : (
          <>
            <Grid item md={4} lg={3} style={{ position: 'relative' }}>
              {/* Sticky wrapper with improved spacing */}
              <div
                style={{
                  position: 'sticky',
                  top: '2rem',
                  maxHeight: 'calc(100vh - 4rem)',
                  paddingBottom: '1rem',
                  overflowY: 'auto',
                }}
              >
                <Paper
                  elevation={2}
                  sx={{
                    borderRadius: '8px',
                    transition: 'box-shadow 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    },
                  }}
                >
                  <SearchSideBar
                    selectedCountry={selectedCountry}
                    setSelectedCountry={setSelectedCountry}
                    selectedLawShortName={selectedLawShortName}
                    setSelectedLawShortName={setSelectedLawShortName}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    selectedYears={selectedYears}
                    setSelectedYears={setSelectedYears}
                    handleSearch={handleSearch}
                    handleReset={handleReset}
                    loading={loading}
                    updateUrlParams={updateUrlParams}
                    setCurrentArticlePage={setCurrentArticlePage}
                    setCurrentDecisionPage={setCurrentDecisionPage}
                    setCurrentFtaGuidePage={setCurrentFtaGuidePage}
                    setResults={setResults}
                    lastFetchTimestamp={lastFetchTimestamp}
                    itemsPerPage={itemsPerPage}
                    searchRequestRef={searchRequestRef}
                    setLoading={setLoading}
                    hasInitializedRef={hasInitializedRef}
                    isUrlTriggeredRef={isUrlTriggeredRef}
                    isAuthenticated={isAuthenticated}
                    autocompleteEnabled={autocompleteEnabled}
                  />
                </Paper>
              </div>
            </Grid>
            <Grid item md={8} lg={9}>
              {/* <Typography variant='h5' component='h1' sx={{ mb: 3 }}>
              Search Results
            </Typography> */}

              {renderActiveFilters()}

              {error && (
                <Alert severity='error' sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Box sx={{ position: 'relative', minHeight: '200px' }}>
                {/* Loading overlay */}
                {loading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(255,255,255,0.7)',
                      zIndex: 5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CircularProgress
                      size={40}
                      sx={{ color: themeColors.primary }}
                    />
                  </Box>
                )}

                {/* Empty state message */}
                {!loading &&
                  !error &&
                  results.articles.length === 0 &&
                  results.decisions.length === 0 &&
                  results.guidelines.length === 0 && (
                    <Alert severity='info' sx={{ mb: 2 }}>
                      {selectedCountry && selectedLawShortName
                        ? 'No results found. Try adjusting your filters.'
                        : 'Please select a country and law to search.'}
                    </Alert>
                  )}

                {/* Results content */}
                {(results.articles.length > 0 ||
                  results.decisions.length > 0 ||
                  results.guidelines.length > 0) && (
                  <Paper sx={{ width: '100%', mb: 3 }}>
                    <Tabs
                      value={tabValue}
                      onChange={handleTabChange}
                      variant='scrollable'
                      scrollButtons='auto'
                      aria-label='search results tabs'
                      sx={{
                        backgroundColor: '#f5f5f5',
                        borderRadius: '8px 8px 0 0',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.03)',
                        position: 'relative',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: '3px',
                          background:
                            'linear-gradient(90deg, #e1e5eb 0%, #f8f9fa 100%)',
                          zIndex: 0,
                        },
                        '& .MuiTabs-flexContainer': {
                          justifyContent: 'center', // Center align tabs
                          gap: '32px', // Even spacing between tabs
                          padding: '0 24px',
                        },
                        '& .MuiTab-root': {
                          textTransform: 'none',
                          fontWeight: 500,
                          minWidth: '160px', // Set a consistent minimum width
                          padding: '12px 24px',
                          transition: 'all 0.3s ease',
                          position: 'relative',
                          overflow: 'hidden',
                          '&:hover': {
                            color: `${themeColors.primary}`,
                            background: 'rgba(248, 249, 250, 0.5)',
                          },
                        },
                        '& .Mui-selected': {
                          color: `${themeColors.primary} !important`,
                          fontWeight: 600,
                          background: 'rgba(248, 249, 250, 0.8)',
                        },
                        '& .MuiTabs-indicator': {
                          backgroundColor: themeColors.primary,
                          height: 3,
                          borderRadius: '3px 3px 0 0',
                          zIndex: 1,
                        },
                      }}
                    >
                      {availableTabs.map((tab, index) => (
                        <Tab
                          key={tab.key}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography>{tab.label}</Typography>
                              <Chip
                                label={tab.count}
                                size='small'
                                sx={{
                                  ml: 1,
                                  height: 24,
                                  fontSize: '0.75rem',
                                  background: `linear-gradient(135deg, ${themeColors.primary} 0%, #3a3e5a 100%)`,
                                  color: 'white',
                                  fontWeight: 'bold',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    transform: 'scale(1.05)',
                                  },
                                }}
                              />
                            </Box>
                          }
                        />
                      ))}
                    </Tabs>

                    <Box
                      sx={{
                        backgroundColor: 'white',
                        borderRadius: '0 0 8px 8px',
                        p: 2,
                        position: 'relative',
                        minHeight: '200px',
                      }}
                    >
                      {availableTabs.map((tab, index) => (
                        <TabPanel key={tab.key} value={tabValue} index={index}>
                          {tab.key === 'articles' && renderArticleResults()}
                          {tab.key === 'decisions' && (
                            <>
                              {renderDecisionTypeTabs()}
                              {renderDecisionResults()}
                            </>
                          )}
                          {tab.key === 'guidelines' && (
                            <>
                              {renderGuidelineTypeTabs()}
                              {renderGuidelineResults()}
                            </>
                          )}
                        </TabPanel>
                      ))}
                    </Box>
                  </Paper>
                )}

                {/* Items per page selector */}
                {(results.articles.length > 0 ||
                  results.decisions.length > 0 ||
                  results.guidelines.length > 0) && (
                  <Box
                    sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}
                  >
                    <ItemsPerPageSelector
                      itemsPerPage={itemsPerPage}
                      setItemsPerPage={setItemsPerPage}
                      loading={loading}
                      themeColors={themeColors}
                    />
                  </Box>
                )}
              </Box>
            </Grid>
          </>
        )}
      </Grid>
      {/* Back to Top button */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 1000,
        }}
      >
        <Zoom in={showBackToTop}>
          {isMediumScreen ? (
            // Small circular Fab for mobile
            <Fab
              size='small'
              aria-label='Scroll back to top'
              onClick={scrollToTop}
              sx={{
                backgroundColor: themeColors.secondary,
                color: themeColors.primary,
                '&:hover': {
                  backgroundColor: 'white',
                  color: themeColors.primary,
                  fontWeight: 'bold',
                },
                '&:active': {
                  backgroundColor: themeColors.secondary,
                  color: themeColors.primary,
                },
                paddingLeft: 2,
                paddingRight: 2,
              }}
            >
              <KeyboardArrowUpIcon fontSize='small' />
            </Fab>
          ) : (
            <Fab
              variant='extended'
              size='medium'
              aria-label='Scroll back to top'
              onClick={scrollToTop}
              sx={{
                backgroundColor: themeColors.secondary,
                color: themeColors.primary,
                '&:hover': {
                  backgroundColor: 'white',
                  color: themeColors.primary,
                  fontWeight: 'bold',
                },
                '&:active': {
                  backgroundColor: themeColors.secondary,
                  color: themeColors.primary,
                },
                paddingLeft: 2,
                paddingRight: 2,
                borderRadius: buttonBorderRadius,
              }}
            >
              <KeyboardArrowUpIcon sx={{ mr: 1 }} />
              <Typography variant='button' sx={{ fontWeight: 'bold' }}>
                Back to Top
              </Typography>
            </Fab>
          )}
        </Zoom>
      </Box>
      {/* Global Snackbar */}
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        autoHideDuration={1500}
        sx={{ zIndex: theme.zIndex.drawer + 2 }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant='filled'
          sx={{
            mt: 5.5,
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            backgroundColor:
              snackbarSeverity === 'warning'
                ? '#ffcf51'
                : snackbarSeverity === 'error'
                  ? '#d32f2f'
                  : snackbarSeverity === 'success'
                    ? '#43a047'
                    : '#232536',
            color: snackbarSeverity === 'warning' ? '#232536' : '#fff',
          }}
        >
          {snackbarMessage || 'Processing your request...'}
        </Alert>
      </Snackbar>
      <NoResultsModal
        open={showNoResultsModal}
        onClose={() => setShowNoResultsModal(false)}
        username={user?.name || user?.username || 'User'}
        searchQuery={noResultsSnapshot.query}
        scopeName={noResultsSnapshot.lawName}
        scopeNoun='law'
      />
    </Container>
  )
}

export default GlobalSearchInLawCSP
